import { useRef } from "react";
import { GemIcon, SpeakerIcon, StarIcon } from "./Icons";
import { useDialogFocusTrap } from "./useDialogFocusTrap";

export interface QuestionDisplay {
  id: string;
  prompt: string;
  expression: string;
  choices: string[];
  correctAnswer: string;
  visualCount?: number;
  visualIcon?: "gem" | "star";
  visualRemoved?: number;
  baseTen?: { tens: number; ones: number };
  numberLine?: { min: number; max: number; highlighted: number[] };
}

interface QuestionModalProps {
  question: QuestionDisplay;
  feedback: "idle" | "wrong" | "correct";
  attempts: number;
  onChoose: (choice: string) => void;
  onSpeak: () => void;
}

export function QuestionModal({ question, feedback, attempts, onChoose, onSpeak }: QuestionModalProps) {
  const panelRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  useDialogFocusTrap(panelRef, titleRef);
  const showAid = attempts >= 2 || question.visualCount !== undefined || question.baseTen !== undefined || question.numberLine !== undefined;
  const message = feedback === "wrong" ? "כמעט! בואי ננסה יחד" : feedback === "correct" ? "מצוין!" : question.prompt;
  const VisualIcon = question.visualIcon === "gem" ? GemIcon : StarIcon;
  const visualName = question.visualIcon === "gem" ? "יהלומים" : "כוכבים";
  const numberLineStep = question.numberLine && question.numberLine.max - question.numberLine.min > 20 ? 10 : 1;
  const numberLineValues = question.numberLine
    ? Array.from(
        { length: Math.floor((question.numberLine.max - question.numberLine.min) / numberLineStep) + 1 },
        (_, index) => question.numberLine!.min + index * numberLineStep
      )
    : [];

  return (
    <div className="modal-layer question-layer" role="dialog" aria-modal="true" aria-labelledby="question-title">
      <section ref={panelRef} className={`storybook-panel question-panel ${feedback}`}>
        <h2 ref={titleRef} id="question-title" tabIndex={-1} aria-live="polite">{message}</h2>
        <div className="math-expression" dir="ltr">{question.expression}</div>
        {showAid && question.visualCount !== undefined ? (
          <div className="visual-aid" aria-label={`${question.visualCount} ${visualName}${question.visualRemoved ? `, מתוכם ${question.visualRemoved} הוסרו` : ""}`}>
            {Array.from({ length: question.visualCount }, (_, index) => {
              const removed = question.visualRemoved !== undefined && index >= question.visualCount! - question.visualRemoved;
              return (
                <span key={index} className={`visual-object ${removed ? "removed" : ""}`}>
                  <VisualIcon className={question.visualIcon === "gem" ? "aid-gem" : "aid-star"} />
                </span>
              );
            })}
          </div>
        ) : null}
        {showAid && question.baseTen ? (
          <div className="base-ten-aid" dir="ltr" aria-label={`${question.baseTen.tens} עשרות ו־${question.baseTen.ones} יחידות`}>
            <div className="ten-rods">{Array.from({ length: question.baseTen.tens }, (_, index) => <span key={index} />)}</div>
            <div className="one-dots">{Array.from({ length: question.baseTen.ones }, (_, index) => <span key={index} />)}</div>
          </div>
        ) : null}
        {showAid && question.numberLine ? (
          <div className="number-line-aid" dir="ltr" aria-label={`קו מספרים מ־${question.numberLine.min} עד ${question.numberLine.max}; המספרים המסומנים הם ${question.numberLine.highlighted.join(", ")}`}>
            {numberLineValues.map((value) => (
              <span key={value} className={question.numberLine!.highlighted.includes(value) ? "highlighted" : ""}>
                <i aria-hidden="true" />
                <b>{value}</b>
              </span>
            ))}
          </div>
        ) : null}
        <div className="answer-row" dir="ltr">
          {question.choices.map((choice) => (
            <button key={choice} className="answer-button" onClick={() => onChoose(choice)} disabled={feedback !== "idle"}>{choice}</button>
          ))}
        </div>
        <button className="speaker-button" onClick={onSpeak}><SpeakerIcon />שוב</button>
      </section>
    </div>
  );
}
